import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import Header from '../shared/Header/Header';
import { privacyPolicyStyles } from '../PrivacyPolicy/PrivacyPolicy.styles';

const TermsAndConditions = () => {
  const styles = privacyPolicyStyles;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headerContainer}>
        <Header />
      </Box>

      <Box sx={styles.contentContainer}>
        <Typography sx={styles.title}>Terms & Conditions</Typography>

        <Box sx={styles.section}>
          <Typography sx={styles.heading}>Royal Vista Vacation Homes Co. LLC</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
            <Typography sx={styles.text}>Last Updated:</Typography>
            <Typography sx={styles.boldText}>10/11/2025</Typography>
          </Box>
          <Typography sx={styles.text}>
            Welcome to the Royal Vista booking platform. These Terms & Conditions govern the use of the Royal Vista
            website and all reservations made through our platform.
          </Typography>
          <Typography sx={styles.text}>
            By accessing this website and making a reservation, you confirm that you have read, understood, and agree
            to comply with the following Terms & Conditions.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>1. Definitions</Typography>

          <Box sx={styles.subsection}>
            <Typography sx={styles.subheading2}>Company / Royal Vista</Typography>
            <Typography sx={styles.text}>
              Refers to Royal Vista Vacation Homes Co. LLC, a holiday homes operator licensed in Dubai, United Arab
              Emirates.
            </Typography>
          </Box>

          <Box sx={styles.subsection}>
            <Typography sx={styles.subheading2}>Holiday Home / Property</Typography>
            <Typography sx={styles.text}>
              Refers to a furnished residential unit licensed for short-term rental in accordance with the Dubai
              Department of Economy and Tourism (DET / DTCM) Holiday Homes regulations.
            </Typography>
          </Box>

          <Box sx={styles.subsection}>
            <Typography sx={styles.subheading2}>Guest / You</Typography>
            <Typography sx={styles.text}>
              Refers to the individual making the booking and all accompanying occupants staying at the property.
            </Typography>
          </Box>

          <Box sx={styles.subsection}>
            <Typography sx={styles.subheading2}>Website / Platform</Typography>
            <Typography sx={styles.text}>
              Refers to the Royal Vista website and online booking system.
            </Typography>
          </Box>

          <Box sx={styles.subsection}>
            <Typography sx={styles.subheading2}>Booking</Typography>
            <Typography sx={styles.text}>
              Refers to a confirmed reservation for accommodation made through the Royal Vista website or associated
              booking platforms.
            </Typography>
          </Box>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>2. Company Information</Typography>
          <Typography sx={styles.text}>
            Royal Vista Vacation Homes Co. LLC operates holiday homes in Dubai in accordance with the applicable
            regulations issued by the Dubai Department of Economy and Tourism (DET).
          </Typography>
          <Typography sx={styles.text}>
            All properties listed by Royal Vista are registered as licensed Holiday Homes in compliance with Dubai
            regulations.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>3. Eligibility to Book</Typography>
          <Typography sx={styles.text}>
            By making a reservation, you confirm that:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>You are at least 18 years old</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>You have the legal authority to enter into a binding agreement</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>You will be present during the stay or will designate a responsible adult occupant</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to refuse or cancel bookings that do not meet eligibility requirements.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>4. Booking Confirmation</Typography>
          <Typography sx={styles.text}>
            A booking is considered confirmed once:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Full payment or required deposit has been received</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>A confirmation email has been issued</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to cancel reservations where payment authorization fails or fraudulent
            activity is suspected.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>5. Payment Terms</Typography>
          <Typography sx={styles.text}>
            All reservations must be paid in full prior to check-in unless otherwise agreed.
          </Typography>
          <Typography sx={styles.text}>
            Accepted payment methods may include:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Credit / Debit Cards</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Online Payment Gateways</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Bank Transfers</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Other approved payment methods</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Prices displayed on the website may include:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Accommodation fees</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Cleaning fees</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Tourism Dirham fee (as mandated by Dubai authorities)</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Applicable taxes or service charges</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to adjust pricing in case of obvious errors or technical issues.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>6. Tourism Dirham Fee</Typography>
          <Typography sx={styles.text}>
            In accordance with Dubai regulations, a Tourism Dirham Fee is charged per bedroom per night and must be
            paid by guests staying in licensed Holiday Homes.
          </Typography>
          <Typography sx={styles.text}>
            This fee is mandated by the Dubai Department of Economy and Tourism (DET) and is remitted directly to the
            government.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>7. Check-In &amp; Check-Out</Typography>
          <Typography sx={styles.text}>
            Standard check-in and check-out times are:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Check-in: from 4:00 PM</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Check-out: by 12:00 AM</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Early check-in or late check-out may be offered subject to availability and may incur additional charges.
          </Typography>
          <Typography sx={styles.text}>
            Guests must provide identification documents as required by Dubai authorities prior to check-in.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>8. Guest Registration Requirements</Typography>
          <Typography sx={styles.text}>
            In accordance with Dubai regulations, Royal Vista is required to register all guests staying in the
            property.
          </Typography>
          <Typography sx={styles.text}>
            Guests must provide:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Passport copy or Emirates ID</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Contact details</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Expected arrival time</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Failure to provide required identification may result in denial of check-in.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>9. Maximum Occupancy</Typography>
          <Typography sx={styles.text}>
            Each property has a maximum permitted occupancy as defined by Dubai Holiday Homes regulations.
          </Typography>
          <Typography sx={styles.text}>
            Guests must not exceed the permitted number of occupants listed in the booking confirmation.
          </Typography>
          <Typography sx={styles.text}>
            Unauthorized additional guests may result in:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Additional charges</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Immediate termination of the stay</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Eviction without refund</Typography></li>
          </Box>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>10. Security Deposit</Typography>
          <Typography sx={styles.text}>
            Royal Vista may require a refundable security deposit prior to check-in.
          </Typography>
          <Typography sx={styles.text}>
            The deposit covers:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Property damage</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Missing items</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Excessive cleaning</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Violations of house rules</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Deposits are typically refunded within 7–14 business days after check-out, subject to inspection.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>11. Guest Responsibilities</Typography>
          <Typography sx={styles.text}>
            Guests agree to:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Respect the property and its furnishings</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Follow all building and community rules</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Maintain reasonable noise levels</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Dispose of waste properly</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Leave the property in reasonable condition upon departure</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Guests are responsible for any damages caused during their stay.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>12. Community Rules &amp; Conduct</Typography>
          <Typography sx={styles.text}>
            All guests must comply with Dubai community regulations and building management rules, including but not
            limited to:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>No parties or events unless explicitly permitted</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>No illegal activities</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Respect quiet hours set by the building</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Compliance with safety regulations</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to terminate the stay without refund if guests violate building rules or
            cause disturbances.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>13. Smoking Policy</Typography>
          <Typography sx={styles.text}>
            Smoking is strictly prohibited inside all Royal Vista properties unless otherwise stated.
          </Typography>
          <Typography sx={styles.text}>
            Smoking in prohibited areas may result in:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Cleaning penalties</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Damage charges</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Security deposit deductions</Typography></li>
          </Box>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>14. Damage and Loss</Typography>
          <Typography sx={styles.text}>
            Guests are responsible for any damage caused during their stay.
          </Typography>
          <Typography sx={styles.text}>
            Charges may apply for:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Broken furniture or appliances</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Missing items</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Lost access cards</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Damage to the property</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Excessive cleaning requirements</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to deduct such costs from the security deposit or charge the payment method
            used for the booking.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>15. Cancellation Policy</Typography>
          <Typography sx={styles.text}>
            Royal Vista operates the following cancellation policy:
          </Typography>
          <Typography sx={styles.text}>
            100% refund for cancellations made at least 14 days prior to the check-in date.
          </Typography>
          <Typography sx={styles.text}>
            For cancellations made less than 14 days before arrival, the booking amount is non-refundable.
          </Typography>
          <Typography sx={styles.text}>
            Refund processing times may vary depending on the payment provider.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>16. Force Majeure</Typography>
          <Typography sx={styles.text}>
            Royal Vista shall not be liable for failure to perform obligations due to circumstances beyond its
            reasonable control, including but not limited to:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Natural disasters</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Government restrictions</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Pandemics</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Utility failures</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Security situations</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Acts of God</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            In such cases, Royal Vista may offer alternative accommodation or booking adjustments where possible.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>17. Website Use</Typography>
          <Typography sx={styles.text}>
            Users agree not to:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Use the website for unlawful purposes</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Attempt to interfere with website functionality</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Submit false or misleading booking information</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to restrict access to users violating these terms.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>18. Third-Party Links</Typography>
          <Typography sx={styles.text}>
            The website may contain links to third-party websites or services. Royal Vista is not responsible for the
            content, privacy policies, or practices of third-party platforms.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>19. Limitation of Liability</Typography>
          <Typography sx={styles.text}>
            To the maximum extent permitted by law, Royal Vista shall not be liable for:
          </Typography>
          <Box component="ul" sx={styles.list}>
            <li><Typography component="span" sx={styles.listItem}>Indirect or consequential losses</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Loss of personal belongings</Typography></li>
            <li><Typography component="span" sx={styles.listItem}>Interruptions due to building or utility issues beyond our control</Typography></li>
          </Box>
          <Typography sx={styles.text}>
            Guests are advised to maintain travel insurance for their stay.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>20. Governing Law</Typography>
          <Typography sx={styles.text}>
            These Terms &amp; Conditions shall be governed by and interpreted in accordance with the laws of the
            United Arab Emirates and the Emirate of Dubai.
          </Typography>
          <Typography sx={styles.text}>
            Any disputes arising in connection with these terms shall fall under the jurisdiction of the courts of
            Dubai.
          </Typography>
        </Box>

        <Box sx={styles.section}>
          <Typography sx={styles.numberedHeading}>21. Amendments to Terms</Typography>
          <Typography sx={styles.text}>
            Royal Vista reserves the right to update these Terms &amp; Conditions at any time.
          </Typography>
          <Typography sx={styles.text}>
            Updated terms will be published on the website and will take effect immediately upon publication.
          </Typography>
        </Box>

        <Box sx={styles.sectionLast}>
          <Typography sx={styles.numberedHeading}>22. Contact Information</Typography>
          <Typography sx={styles.text}>
            For any questions regarding these Terms &amp; Conditions, please contact:
          </Typography>
          <Typography sx={styles.text}>
            Royal Vista Vacation Homes Co. LLC
          </Typography>
          <Typography sx={styles.text}>
            Email:{' '}
            <Link href="mailto:contact@royal-vista.com" sx={styles.link}>
              contact@royal-vista.com
            </Link>
          </Typography>
          <Typography sx={styles.text}>
            Website:{' '}
            <Link href="https://www.royal-vista.com" sx={styles.link}>
              www.royal-vista.com
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TermsAndConditions;
